import { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Close, Circle } from "@mui/icons-material";
import theme from "../../theme/theme";

const STATUS_COLORS = {
  completed: { bg: "#E8F5E9", color: "#2E7D32", label: "Completed" },
  pending: { bg: "#E3F2FD", color: "#1565C0", label: "Pending" },
  in_progress: { bg: "#FFF8E1", color: "#F57F17", label: "In Progress" },
  cancelled: { bg: "#FFEBEE", color: "#C62828", label: "Cancelled" },
  unassigned: {
    bg: "#FFF3E0",
    color: "#E65100",
    label: "Pending (Unassigned)",
  },
  delayed: { bg: "#FCE4EC", color: "#AD1457", label: "Delayed" },
};

const getDisplayStatus = (entry) => {
  if (entry.service_status === "completed") return "completed";
  if (entry.service_status === "in_progress") return "in_progress";
  if (entry.service_status === "cancelled") return "cancelled";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const serviceDate = new Date(entry.service_date);
  serviceDate.setHours(0, 0, 0, 0);
  if (entry.service_status === "pending" && serviceDate < today)
    return "delayed";
  if (entry.service_status === "pending" && !entry.service_by)
    return "unassigned";
  return "pending";
};

const ServiceEntryDialog = ({
  open,
  onClose,
  entry,
  employees = [],
  onSave,
  saving = false,
}) => {
  const [form, setForm] = useState({
    service_date: "",
    service_by: "",
    service_status: "",
  });

  useEffect(() => {
    if (entry) {
      setForm({
        service_date: entry.service_date?.split("T")[0] || "",
        service_by: entry.service_by || "",
        service_status: entry.service_status || "",
      });
    }
  }, [entry]);

  if (!entry) return null;

  const displayStatus = getDisplayStatus(entry);
  const statusDef = STATUS_COLORS[displayStatus];
  const isPending = entry.service_status === "pending";

  const handleSave = () => {
    const payload = {};
    if (isPending) {
      if (form.service_date !== (entry.service_date?.split("T")[0] || "")) {
        payload.service_date = form.service_date;
      }
      if (form.service_by !== (entry.service_by || "")) {
        payload.service_by = form.service_by || null;
      }
    }
    if (form.service_status !== entry.service_status) {
      payload.service_status = form.service_status;
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    onSave(entry.id, payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: theme.dialog.borderRadius } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          backgroundColor: theme.dialog.headerBgColor,
        }}
      >
        <Typography variant="h6" fontWeight="700">
          Service Entry
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {/* Current Status Badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" fontWeight="500">
            Status:
          </Typography>
          <Chip
            icon={<Circle sx={{ fontSize: "10px !important" }} />}
            label={statusDef.label}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: statusDef.bg,
              color: statusDef.color,
              borderColor: statusDef.color + "40",
              border: "1px solid",
              "& .MuiChip-icon": { color: statusDef.color },
            }}
          />
        </Box>

        {/* Status Change */}
        <TextField
          label="Change Status"
          select
          fullWidth
          size="small"
          value={form.service_status}
          onChange={(e) => setForm({ ...form, service_status: e.target.value })}
        >
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>

        {/* Date — editable only if pending */}
        <TextField
          label="Service Date"
          type="date"
          fullWidth
          size="small"
          value={form.service_date}
          onChange={(e) => setForm({ ...form, service_date: e.target.value })}
          InputLabelProps={{ shrink: true }}
          disabled={!isPending}
          helperText={
            !isPending ? "Date can only be changed for pending entries" : ""
          }
        />

        {/* Assigned Employee — editable only if pending */}
        <TextField
          label="Assigned Employee"
          select
          fullWidth
          size="small"
          value={form.service_by}
          onChange={(e) => setForm({ ...form, service_by: e.target.value })}
          disabled={!isPending}
          helperText={
            !isPending
              ? "Employee can only be changed for pending entries"
              : !form.service_by
                ? "No employee assigned yet"
                : ""
          }
        >
          <MenuItem value="">
            <em>Not Assigned</em>
          </MenuItem>
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Read-only info */}
        {entry.service_employee?.name && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: "#f8f9fa",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>Currently Assigned:</strong> {entry.service_employee.name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
          backgroundColor: theme.dialog.footerBgColor,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            backgroundColor: theme.palette.primary.main,
          }}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ServiceEntryDialog;
